import { db, auth, functions } from '@/js/firebase';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { logger } from './loggerService';
import { toastBridge } from './toastBridge';

/**
 * Automation Rules Service
 * Handles CRUD operations for automation rules
 * Collection: AutomationRules
 */
class AutomationRulesService {
  constructor() {
    this.logger = logger;
    this.toast = toastBridge;
    this.collectionName = 'AutomationRules';
  }

  /**
   * Create a new automation rule
   * @param {Object} ruleData - The rule data
   * @returns {Promise<{success: boolean, id: string}>}
   */
  async createRule(ruleData) {
    try {
      const rulesRef = collection(db, this.collectionName);
      const user = auth.currentUser;

      const newRule = {
        name: ruleData.name,
        description: ruleData.description || '',
        category: ruleData.category || '',
        enabled: ruleData.enabled !== false,
        trigger: ruleData.trigger,
        conditions: ruleData.conditions || [],
        // Without this the builder's ANY/ALL toggle is dropped on create (update
        // spreads it, so it only bit new and cloned rules) and the engine defaults
        // to 'all' — an ANY rule would silently save as AND.
        conditionLogic: ruleData.conditionLogic || 'all',
        actions: ruleData.actions || [],
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        createdByName: user?.displayName || 'Unknown',
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
        updatedByName: user?.displayName || 'Unknown',
        lastExecutedAt: null,
        executionCount: 0
      };

      const docRef = await addDoc(rulesRef, newRule);

      this.logger.info('[AutomationRulesService] Rule created', {
        ruleId: docRef.id,
        ruleName: ruleData.name
      });
      this.toast.success('Automation rule created successfully');

      return { success: true, id: docRef.id };
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to create rule', {
        error: error.message
      });
      this.toast.error('Failed to create automation rule');
      throw error;
    }
  }

  /**
   * Get a single rule by ID
   * @param {string} ruleId - The rule ID
   * @returns {Promise<Object|null>}
   */
  async getRule(ruleId) {
    try {
      const ruleRef = doc(db, this.collectionName, ruleId);
      const ruleSnap = await getDoc(ruleRef);

      if (ruleSnap.exists()) {
        return { id: ruleSnap.id, ...ruleSnap.data() };
      }

      return null;
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to get rule', {
        error: error.message,
        ruleId
      });
      throw error;
    }
  }

  /**
   * Get all automation rules
   * @returns {Promise<Array>}
   */
  async getAllRules() {
    try {
      const rulesRef = collection(db, this.collectionName);
      const q = query(rulesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const rules = [];
      snapshot.forEach((doc) => {
        rules.push({ id: doc.id, ...doc.data() });
      });

      this.logger.info('[AutomationRulesService] Fetched all rules', {
        count: rules.length
      });

      return rules;
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to get all rules', {
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Get all enabled automation rules
   * @returns {Promise<Array>}
   */
  async getEnabledRules() {
    try {
      const rulesRef = collection(db, this.collectionName);
      const q = query(rulesRef, where('enabled', '==', true));
      const snapshot = await getDocs(q);

      const rules = [];
      snapshot.forEach((doc) => {
        rules.push({ id: doc.id, ...doc.data() });
      });

      return rules;
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to get enabled rules', {
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Get rules by trigger type
   * @param {string} triggerType - field_change, date_based, calendar_event, or conversation
   * @returns {Promise<Array>}
   */
  async getRulesByTriggerType(triggerType) {
    try {
      const rulesRef = collection(db, this.collectionName);
      const q = query(
        rulesRef,
        where('trigger.type', '==', triggerType),
        where('enabled', '==', true)
      );
      const snapshot = await getDocs(q);

      const rules = [];
      snapshot.forEach((doc) => {
        rules.push({ id: doc.id, ...doc.data() });
      });

      return rules;
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to get rules by trigger type', {
        error: error.message,
        triggerType
      });
      throw error;
    }
  }

  /**
   * Update an existing rule
   * @param {string} ruleId - The rule ID
   * @param {Object} updates - The updates to apply
   * @param {boolean} showToast - Whether to show toast notification
   * @returns {Promise<{success: boolean}>}
   */
  async updateRule(ruleId, updates, showToast = true) {
    try {
      const ruleRef = doc(db, this.collectionName, ruleId);
      const user = auth.currentUser;

      const cleanedUpdates = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
        updatedByName: user?.displayName || 'Unknown'
      };

      // Remove fields that shouldn't be updated
      delete cleanedUpdates.createdAt;
      delete cleanedUpdates.createdBy;
      delete cleanedUpdates.createdByName;
      delete cleanedUpdates.id;

      await updateDoc(ruleRef, cleanedUpdates);

      this.logger.info('[AutomationRulesService] Rule updated', {
        ruleId,
        updates: Object.keys(updates)
      });

      if (showToast) {
        this.toast.success('Automation rule updated');
      }

      return { success: true };
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to update rule', {
        error: error.message,
        ruleId
      });
      this.toast.error('Failed to update automation rule');
      throw error;
    }
  }

  /**
   * Toggle rule enabled state
   * @param {string} ruleId - The rule ID
   * @param {boolean} enabled - The new enabled state
   * @returns {Promise<{success: boolean}>}
   */
  async toggleRuleEnabled(ruleId, enabled) {
    await this.updateRule(ruleId, { enabled }, false);
    this.toast.success(enabled ? 'Rule enabled' : 'Rule disabled');
    return { success: true };
  }

  /**
   * Delete a rule
   * @param {string} ruleId - The rule ID
   * @returns {Promise<{success: boolean}>}
   */
  async deleteRule(ruleId) {
    try {
      const ruleRef = doc(db, this.collectionName, ruleId);
      await deleteDoc(ruleRef);

      this.logger.info('[AutomationRulesService] Rule deleted', { ruleId });
      this.toast.success('Automation rule deleted');

      return { success: true };
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to delete rule', {
        error: error.message,
        ruleId
      });
      this.toast.error('Failed to delete automation rule');
      throw error;
    }
  }

  /**
   * Subscribe to all rules with real-time updates
   * @param {Function} callback - Callback function receiving rules array
   * @returns {Function} Unsubscribe function
   */
  subscribeToRules(callback) {
    const rulesRef = collection(db, this.collectionName);
    const q = query(rulesRef, orderBy('createdAt', 'desc'));

    return onSnapshot(q, (snapshot) => {
      const rules = [];
      snapshot.forEach((doc) => {
        rules.push({ id: doc.id, ...doc.data() });
      });
      callback(rules);
    }, (error) => {
      this.logger.error('[AutomationRulesService] Subscription error', {
        error: error.message
      });
    });
  }

  /**
   * Record a rule execution
   * @param {string} ruleId - The rule ID
   * @param {Object} executionData - Details about the execution
   * @returns {Promise<{success: boolean}>}
   */
  async recordExecution(ruleId, executionData) {
    try {
      const ruleRef = doc(db, this.collectionName, ruleId);

      // Update the rule's execution metadata
      await updateDoc(ruleRef, {
        lastExecutedAt: serverTimestamp(),
        executionCount: (executionData.previousCount || 0) + 1
      });

      // Log to execution history subcollection
      const historyRef = collection(db, this.collectionName, ruleId, 'executionHistory');
      await addDoc(historyRef, {
        timestamp: serverTimestamp(),
        deliverableId: executionData.deliverableId,
        deliverableTitle: executionData.deliverableTitle,
        triggerData: executionData.triggerData,
        actionsExecuted: executionData.actionsExecuted,
        success: executionData.success,
        error: executionData.error || null
      });

      this.logger.info('[AutomationRulesService] Execution recorded', {
        ruleId,
        deliverableId: executionData.deliverableId
      });

      return { success: true };
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to record execution', {
        error: error.message,
        ruleId
      });
      // Don't throw - execution recording failure shouldn't break the automation
      return { success: false };
    }
  }

  /**
   * Get execution history for a rule
   * @param {string} ruleId - The rule ID
   * @param {number} limitCount - Max number of records to fetch
   * @returns {Promise<Array>}
   */
  async getExecutionHistory(ruleId, limitCount = 50) {
    try {
      const historyRef = collection(db, this.collectionName, ruleId, 'executionHistory');
      const q = query(historyRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);

      const history = [];
      let count = 0;
      snapshot.forEach((doc) => {
        if (count < limitCount) {
          history.push({ id: doc.id, ...doc.data() });
          count++;
        }
      });

      return history;
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to get execution history', {
        error: error.message,
        ruleId
      });
      throw error;
    }
  }

  /**
   * Subscribe to execution history for a rule
   * @param {string} ruleId - The rule ID
   * @param {Function} callback - Callback function receiving history array
   * @returns {Function} Unsubscribe function
   */
  subscribeToExecutionHistory(ruleId, callback) {
    const historyRef = collection(db, this.collectionName, ruleId, 'executionHistory');
    const q = query(historyRef, orderBy('timestamp', 'desc'));

    return onSnapshot(q, (snapshot) => {
      const history = [];
      snapshot.forEach((doc) => {
        history.push({ id: doc.id, ...doc.data() });
      });
      callback(history);
    }, (error) => {
      this.logger.error('[AutomationRulesService] History subscription error', {
        error: error.message,
        ruleId
      });
    });
  }

  /**
   * Log an error to the rule's errorLogs subcollection
   * @param {string} ruleId - The rule ID
   * @param {Object} errorData - Error details
   * @returns {Promise<{success: boolean, shouldDisable: boolean}>}
   */
  async logError(ruleId, errorData) {
    try {
      const ruleRef = doc(db, this.collectionName, ruleId);
      const errorLogsRef = collection(db, this.collectionName, ruleId, 'errorLogs');

      // Add error to subcollection
      await addDoc(errorLogsRef, {
        timestamp: serverTimestamp(),
        message: errorData.message || 'Unknown error',
        deliverableId: errorData.deliverableId || null,
        deliverableTitle: errorData.deliverableTitle || null,
        triggerData: errorData.triggerData || null,
        stack: errorData.stack || null,
        context: errorData.context || null
      });

      // Increment error count on rule document
      const ruleSnap = await getDoc(ruleRef);
      const currentCount = ruleSnap.data()?.errorCount || 0;
      const newCount = currentCount + 1;

      const updates = {
        errorCount: newCount,
        lastErrorAt: serverTimestamp()
      };

      // Auto-disable if error count reaches threshold (5+)
      const shouldDisable = newCount >= 5;
      if (shouldDisable && ruleSnap.data()?.enabled) {
        updates.enabled = false;
        updates.disabledReason = 'Auto-disabled due to repeated errors';
        this.logger.warn('[AutomationRulesService] Rule auto-disabled due to errors', {
          ruleId,
          errorCount: newCount
        });
      }

      await updateDoc(ruleRef, updates);

      this.logger.info('[AutomationRulesService] Error logged', {
        ruleId,
        errorCount: newCount,
        shouldDisable
      });

      return { success: true, shouldDisable };
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to log error', {
        error: error.message,
        ruleId
      });
      return { success: false, shouldDisable: false };
    }
  }

  /**
   * Get error logs for a rule
   * @param {string} ruleId - The rule ID
   * @param {number} limitCount - Max number of records to fetch
   * @returns {Promise<Array>}
   */
  async getErrorLogs(ruleId, limitCount = 50) {
    try {
      const errorLogsRef = collection(db, this.collectionName, ruleId, 'errorLogs');
      const q = query(errorLogsRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);

      const errors = [];
      let count = 0;
      snapshot.forEach((doc) => {
        if (count < limitCount) {
          errors.push({ id: doc.id, ...doc.data() });
          count++;
        }
      });

      return errors;
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to get error logs', {
        error: error.message,
        ruleId
      });
      throw error;
    }
  }

  /**
   * Subscribe to error logs for a rule
   * @param {string} ruleId - The rule ID
   * @param {Function} callback - Callback function receiving errors array
   * @returns {Function} Unsubscribe function
   */
  subscribeToErrorLogs(ruleId, callback) {
    const errorLogsRef = collection(db, this.collectionName, ruleId, 'errorLogs');
    const q = query(errorLogsRef, orderBy('timestamp', 'desc'));

    return onSnapshot(q, (snapshot) => {
      const errors = [];
      snapshot.forEach((doc) => {
        errors.push({ id: doc.id, ...doc.data() });
      });
      callback(errors);
    }, (error) => {
      this.logger.error('[AutomationRulesService] Error logs subscription error', {
        error: error.message,
        ruleId
      });
    });
  }

  /**
   * Clear error logs and reset error count for a rule
   * @param {string} ruleId - The rule ID
   * @returns {Promise<{success: boolean}>}
   */
  async clearErrors(ruleId) {
    try {
      const ruleRef = doc(db, this.collectionName, ruleId);
      const errorLogsRef = collection(db, this.collectionName, ruleId, 'errorLogs');

      // Delete all error logs
      const snapshot = await getDocs(errorLogsRef);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Reset error count on rule
      await updateDoc(ruleRef, {
        errorCount: 0,
        lastErrorAt: null,
        disabledReason: null
      });

      this.logger.info('[AutomationRulesService] Errors cleared', { ruleId });
      this.toast.success('Error logs cleared');

      return { success: true };
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to clear errors', {
        error: error.message,
        ruleId
      });
      this.toast.error('Failed to clear error logs');
      throw error;
    }
  }

  /**
   * Get total error count across all rules
   * @returns {Promise<number>}
   */
  async getTotalErrorCount() {
    try {
      const rulesRef = collection(db, this.collectionName);
      const snapshot = await getDocs(rulesRef);

      let totalErrors = 0;
      snapshot.forEach((doc) => {
        totalErrors += doc.data()?.errorCount || 0;
      });

      return totalErrors;
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to get total error count', {
        error: error.message
      });
      return 0;
    }
  }

  /**
   * Bulk update category for multiple rules
   * @param {Array<string>} ruleIds - Array of rule IDs
   * @param {string} category - The category to assign
   * @returns {Promise<{success: boolean}>}
   */
  async bulkUpdateCategory(ruleIds, category) {
    try {
      const batch = writeBatch(db);
      const user = auth.currentUser;

      ruleIds.forEach(id => {
        const ruleRef = doc(db, this.collectionName, id);
        batch.update(ruleRef, {
          category,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
          updatedByName: user?.displayName || 'Unknown'
        });
      });

      await batch.commit();

      this.logger.info('[AutomationRulesService] Bulk category updated', {
        count: ruleIds.length,
        category
      });
      this.toast.success(`${ruleIds.length} rule${ruleIds.length !== 1 ? 's' : ''} moved to "${category || 'Uncategorized'}"`);

      return { success: true };
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to bulk update category', {
        error: error.message
      });
      this.toast.error('Failed to update categories');
      throw error;
    }
  }

  /**
   * Execute an action button (button_click trigger)
   * @param {string} ruleId - The rule ID
   * @param {string} deliverableId - The deliverable ID
   * @returns {Promise<Object>} Result from the Cloud Function
   */
  async executeActionButton(ruleId, deliverableId) {
    try {
      this.logger.info('[AutomationRulesService] Executing action button', {
        ruleId,
        deliverableId
      });

      const executeActionButtonFn = httpsCallable(functions, 'executeActionButton');
      const result = await executeActionButtonFn({ ruleId, deliverableId });

      this.logger.info('[AutomationRulesService] Action button executed', {
        ruleId,
        deliverableId,
        success: result.data?.success
      });

      return result.data;
    } catch (error) {
      this.logger.error('[AutomationRulesService] Failed to execute action button', {
        error: error.message,
        ruleId,
        deliverableId
      });
      throw error;
    }
  }
}

// Export singleton instance
export const automationRulesService = new AutomationRulesService();
